import { CircleCheck, Clock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { RentModel } from 'types/api'

import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

// `flat` + `flat_title` are returned by the rent serializer (regenerate API
// types to surface them; typed here until then).
type RentRow = RentModel & { flat?: string | null; flat_title?: string | null }

const TH =
  'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'
const TD = 'px-4 py-4 align-middle text-sm text-ink'

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('sk-SK') : '—'

function StatusPill({ paid }: { paid?: boolean }) {
  const { t } = useTranslation('contract')
  return paid ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-0.5 text-xs font-semibold text-success">
      <CircleCheck className="h-3.5 w-3.5" />
      {t('rent_table.rents.paid')}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-peach/30 px-2.5 py-0.5 text-xs font-semibold text-warning">
      <Clock className="h-3.5 w-3.5" />
      {t('rent_table.rents.unpaid')}
    </span>
  )
}

interface LandlordPaymentsTableProps {
  rents: RentModel[] | undefined
  isLoading?: boolean
  /** Show a column linking to the flat (for the cross-flat payments page). */
  showFlat?: boolean
}

/** Read-only payments list shared by the landlord payments page and flat detail. */
export function LandlordPaymentsTable({
  rents,
  isLoading = false,
  showFlat = false
}: LandlordPaymentsTableProps) {
  const { t: tc } = useTranslation('contract')

  // Unpaid first, then by due date (most recent first).
  const rows = ([...((rents as RentRow[]) ?? [])]).sort(
    (a, b) =>
      Number(a.paid) - Number(b.paid) ||
      (b.due_date ?? '').localeCompare(a.due_date ?? '')
  )

  return (
    <Card className="overflow-hidden p-0">
      {isLoading ? (
        <div className="h-40 animate-pulse bg-ink/[0.04]" />
      ) : rows.length === 0 ? (
        <div className="px-6 py-12 text-center text-sm text-muted-foreground">
          {tc('rent_table.rents.no_actual_payments', {
            defaultValue: 'No payments'
          })}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-ink/[0.08] bg-ink/[0.02] text-left">
                <th className={TH}>{tc('rent_table.rents.type')}</th>
                {showFlat && (
                  <th className={TH}>{tc('rent_table.rents.flat')}</th>
                )}
                <th className={TH}>{tc('rent_table.rents.variable_symbol')}</th>
                <th className={TH}>{tc('rent_table.rents.specific_symbol')}</th>
                <th className={TH}>{tc('rent_table.rents.amount')}</th>
                <th className={TH}>{tc('rent_table.rents.due_date')}</th>
                <th className={TH}>{tc('rent_table.rents.paid_date')}</th>
                <th className={TH}>{tc('rent_table.rents.status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((rent: RentRow) => (
                <tr
                  key={rent.id}
                  className="border-b border-ink/[0.05] transition-colors last:border-0 hover:bg-ink/[0.015]"
                >
                  <td className={cn(TD, 'font-medium')}>
                    {tc(`rent_table.rents.${rent.payment_type}`, {
                      defaultValue: rent.payment_type
                    })}
                  </td>
                  {showFlat && (
                    <td className={TD}>
                      {rent.flat ? (
                        <Link
                          to={`/landlord/flats/${rent.flat}`}
                          className="text-primary transition-colors hover:underline"
                        >
                          {rent.flat_title || rent.flat}
                        </Link>
                      ) : (
                        <span className="text-ink/40">—</span>
                      )}
                    </td>
                  )}
                  <td className={cn(TD, 'whitespace-nowrap')}>
                    {rent.variable_symbol || '—'}
                  </td>
                  <td className={cn(TD, 'whitespace-nowrap')}>
                    {rent.specific_symbol || '—'}
                  </td>
                  <td className={cn(TD, 'whitespace-nowrap font-semibold')}>
                    {Number(rent.amount).toFixed(2)} €
                  </td>
                  <td className={cn(TD, 'whitespace-nowrap')}>
                    {formatDate(rent.due_date)}
                  </td>
                  <td className={cn(TD, 'whitespace-nowrap')}>
                    {formatDate(rent.paid_date)}
                  </td>
                  <td className={TD}>
                    <StatusPill paid={rent.paid} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
